import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  fetchDispatchBoard,
  postNote,
  rescheduleVisit,
  type DispatchBlock,
  type DispatchBoardResponse,
  type DispatchTech,
  type ScheduleSlotResult,
} from "../api";
import { useSlotsQuery, useInvalidateSlots } from "../hooks/useSlotsQuery.js";

// ---------------------------------------------------------------------------
// Dispatch Board 5 — full-screen modal for human-handover scheduling.
// Three sections (mirrors DB3):
//   §1 Customer preferences (compact, advanced collapsed by default)
//   §2 Best slots — one row per cleaner per day with "+ N more" expander
//   §3 Day grid: tech-as-columns, visits + free/busy + merged algo overlay
// ---------------------------------------------------------------------------

const OVER_THRESHOLD_MIN = 35; // ">35 min drive" cutoff (was DB4's purple bucket)

const DAY_START_HOUR = 8;
const DAY_END_HOUR = 19;
const PIXELS_PER_MIN = 1.0;
const COL_HEIGHT = (DAY_END_HOUR - DAY_START_HOUR) * 60 * PIXELS_PER_MIN;
const HOUR_LABELS = Array.from({ length: DAY_END_HOUR - DAY_START_HOUR + 1 }, (_, i) => DAY_START_HOUR + i);

// ─── helpers ──────────────────────────────────────────────────────────────

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function parseIsoDate(s: string): Date {
  const datePart = s.split("T")[0] ?? s;
  const [y, m, d] = datePart.split("-").map(Number);
  return new Date(y!, m! - 1, d!);
}
function fmtDayHeader(s: string): string {
  const d = parseIsoDate(s);
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}
function fmtClipboardLine(iso: string): string {
  const d = parseIsoDate(iso);
  const date = d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const time = iso.slice(11, 16);
  return `${date} ${time}`;
}
function isoHM(iso: string): string {
  return (iso.split("T")[1] ?? "").slice(0, 5);
}
function minutesFromDayStart(iso: string): number {
  const t = iso.split("T")[1] ?? "00:00:00";
  const [h, m] = t.split(":").map(Number);
  return ((h ?? 0) - DAY_START_HOUR) * 60 + (m ?? 0);
}
function clampToCol(minStart: number, minEnd: number): { top: number; height: number } | null {
  const colMin = 0;
  const colMax = (DAY_END_HOUR - DAY_START_HOUR) * 60;
  const a = Math.max(colMin, Math.min(colMax, minStart));
  const b = Math.max(colMin, Math.min(colMax, minEnd));
  if (b <= a) return null;
  return { top: a * PIXELS_PER_MIN, height: (b - a) * PIXELS_PER_MIN };
}
function fmtHrs(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h${m.toString().padStart(2, "0")}`;
}
function shiftDate(s: string, deltaDays: number): string {
  const d = parseIsoDate(s);
  d.setDate(d.getDate() + deltaDays);
  return isoDate(d);
}
function todayIso(): string {
  return isoDate(new Date());
}
function snap30(minutes: number): number {
  return Math.round(minutes / 30) * 30;
}
function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}
function combineLocal(date: string, minutesFromDayStart: number): string {
  const totalMin = DAY_START_HOUR * 60 + minutesFromDayStart;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${date}T${pad2(h)}:${pad2(m)}:00`;
}
function snapHmTo30(hm: string): string {
  const [hStr, mStr] = hm.split(":");
  const total = (Number(hStr) || 0) * 60 + (Number(mStr) || 0);
  const snapped = snap30(total);
  return `${pad2(Math.floor(snapped / 60))}:${pad2(snapped % 60)}`;
}

// Mirror of FastAPI's effective_job_minutes() in slot_finder.py — kept in
// sync so the live-displayed effective time in §1 matches what the API
// actually books. Cleaning time → committed time: +30 min overhead, floor
// at 60, round to nearest 30. End-times always land on :00 or :30.
const JOB_OVERHEAD_MINUTES = 30;
const MIN_JOB_MINUTES = 60;
function effectiveJobMinutes(cleaningMinutes: number): number {
  const total = cleaningMinutes + JOB_OVERHEAD_MINUTES;
  const bounded = Math.max(MIN_JOB_MINUTES, total);
  return Math.round(bounded / 30) * 30;
}
function fmtHrsMins(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} hr`;
  return `${h} hr ${m} min`;
}

// ─── §1 Customer Preferences ─────────────────────────────────────────────
//
// The 4 always-visible fields (postcode, est. job time, total value, notes)
// are the only ones that affect the slot search. The 4 radios in the
// "Advanced preferences" expander are kept around as scratch state for the
// human agent but don't yet hit the API — TODO: wire to PreferenceWindow
// when the algo supports it.

interface PrefsState {
  flexAsap: "asap" | "flexible" | "";
  rangeOrSpecific: "date_range" | "specific_dates" | "";
  rangeStart: string;
  rangeEnd: string;
  daysMode: "mon_to_sun" | "specific_days" | "";
  specificDays: Set<number>;
  startTimeMode: "any" | "specific" | "";
  specificStart: string;
  notes: string;
}

const EMPTY_PREFS: PrefsState = {
  flexAsap: "",
  rangeOrSpecific: "",
  rangeStart: todayIso(),
  rangeEnd: shiftDate(todayIso(), 60),
  daysMode: "",
  specificDays: new Set(),
  startTimeMode: "",
  specificStart: "",
  notes: "",
};

function PreferencesPanel({
  postcode,
  setPostcode,
  durationMinutes,
  setDurationMinutes,
  estimatedValueGbp,
  setEstimatedValueGbp,
  prefs,
  setPrefs,
  onSearch,
  searching,
  onSaveNote,
  noteSaving,
  noteSaved,
  noteError,
  canSaveNote,
}: {
  postcode: string;
  setPostcode: (v: string) => void;
  durationMinutes: number;
  setDurationMinutes: (v: number) => void;
  estimatedValueGbp: number;
  setEstimatedValueGbp: (v: number) => void;
  prefs: PrefsState;
  setPrefs: (p: PrefsState) => void;
  onSearch: () => void;
  searching: boolean;
  onSaveNote: () => void;
  noteSaving: boolean;
  noteSaved: boolean;
  noteError: string | null;
  canSaveNote: boolean;
}): JSX.Element {
  const [advancedOpen, setAdvancedOpen] = useState<boolean>(false);
  const dayLabels = ["S", "M", "T", "W", "T", "F", "S"];
  function toggleDay(idx: number): void {
    const next = new Set(prefs.specificDays);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    setPrefs({ ...prefs, specificDays: next });
  }
  const inputCls =
    "w-full rounded-md border border-ink-400 bg-ink-900 px-2 py-1 text-[13px] text-ink-50 focus:border-emerald-500 focus:outline-none";
  return (
    <section className="border-b border-ink-600 bg-ink-900 px-4 py-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
          Customer preferences
        </div>
        <button
          type="button"
          onClick={onSearch}
          disabled={!postcode.trim() || searching}
          className="rounded-md border border-emerald-600 bg-emerald-700 px-3 py-1.5 text-[13px] font-medium text-ink-50 hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {searching ? "Searching…" : "Find slots"}
        </button>
      </div>

      {/* Row 1: postcode · cleaning time · total job value */}
      <div className="grid gap-3 md:grid-cols-[140px_220px_140px_1fr]">
        <label className="block">
          <span className="block text-[13px] text-ink-100">Postcode</span>
          <input
            type="text"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value.toUpperCase())}
            className={inputCls}
            placeholder="e.g. TW7 7EQ"
          />
        </label>
        <label className="block">
          <span className="block text-[13px] text-ink-100">
            Cleaning time (min)
            <span className="ml-2 font-normal text-emerald-300">
              · Job time {fmtHrsMins(effectiveJobMinutes(durationMinutes))}
            </span>
          </span>
          <input
            type="number"
            value={durationMinutes}
            onChange={(e) => setDurationMinutes(Math.max(15, Number(e.target.value) || 60))}
            className={inputCls}
            title="Raw cleaning time. We add 30 min overhead, enforce a 1-hour minimum, and round to the nearest half hour."
          />
        </label>
        <label className="block">
          <span className="block text-[13px] text-ink-100">Total job value (£)</span>
          <input
            type="number"
            value={estimatedValueGbp}
            onChange={(e) => setEstimatedValueGbp(Math.max(0, Number(e.target.value) || 0))}
            className={inputCls}
          />
        </label>
        <div />
      </div>

      {/* Row 2: discussed date/time notes + save button — full width */}
      <div className="mt-3 block">
        <span className="block text-[13px] text-ink-100">Discussed date/time notes</span>
        <div className="flex items-start gap-2">
          <textarea
            rows={2}
            value={prefs.notes}
            onChange={(e) => setPrefs({ ...prefs, notes: e.target.value })}
            className={inputCls + " flex-1 resize-y"}
            placeholder="e.g. only Saturdays after 1pm"
          />
          <div className="flex shrink-0 flex-col items-stretch gap-1">
            <button
              type="button"
              onClick={onSaveNote}
              disabled={!canSaveNote || noteSaving || !prefs.notes.trim()}
              className="whitespace-nowrap rounded-md border border-emerald-600 bg-emerald-700/70 px-3 py-1 text-[13px] font-medium text-ink-50 hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
              title={canSaveNote ? "Save as a note on this card" : "Card not saved yet"}
            >
              {noteSaving ? "Saving…" : "Save note"}
            </button>
            {noteSaved ? (
              <span className="rounded bg-emerald-700/40 px-2 py-0.5 text-center text-[12px] text-emerald-100">Saved ✓</span>
            ) : null}
            {noteError ? (
              <span className="rounded bg-red-700/40 px-2 py-0.5 text-center text-[12px] text-red-100" title={noteError}>
                Save failed
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Advanced preferences (collapsed by default) */}
      <div className="mt-2">
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          className="text-[13px] text-ink-100 hover:text-ink-100"
        >
          {advancedOpen ? "▾" : "▸"} Advanced preferences
        </button>
        {advancedOpen ? (
          <div className="mt-2 rounded-md border border-ink-600 bg-ink-900/60 px-3 py-3">
            <div className="mb-2 grid grid-cols-4 gap-2 text-[12px] text-ink-100">
              <RadioPair
                label="When"
                value={prefs.flexAsap}
                onChange={(v) => setPrefs({ ...prefs, flexAsap: v as PrefsState["flexAsap"] })}
                options={[{ v: "asap", l: "ASAP" }, { v: "flexible", l: "Flexible" }]}
              />
              <RadioPair
                label="Date scope"
                value={prefs.rangeOrSpecific}
                onChange={(v) => setPrefs({ ...prefs, rangeOrSpecific: v as PrefsState["rangeOrSpecific"] })}
                options={[{ v: "date_range", l: "Date range" }, { v: "specific_dates", l: "Specific dates" }]}
              />
              <RadioPair
                label="Days"
                value={prefs.daysMode}
                onChange={(v) => setPrefs({ ...prefs, daysMode: v as PrefsState["daysMode"] })}
                options={[{ v: "mon_to_sun", l: "Mon–Sun" }, { v: "specific_days", l: "Specific days" }]}
              />
              <RadioPair
                label="Start time"
                value={prefs.startTimeMode}
                onChange={(v) => setPrefs({ ...prefs, startTimeMode: v as PrefsState["startTimeMode"] })}
                options={[{ v: "any", l: "Any start" }, { v: "specific", l: "Specific start" }]}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {prefs.rangeOrSpecific === "date_range" ? (
                <div className="flex items-end gap-2">
                  <label className="flex-1">
                    <span className="block text-[13px] text-ink-100">From</span>
                    <input
                      type="date"
                      value={prefs.rangeStart}
                      onChange={(e) => setPrefs({ ...prefs, rangeStart: e.target.value })}
                      className="w-full rounded-md border border-ink-400 bg-ink-900 px-2 py-1 text-[12px] text-ink-50 focus:border-emerald-500 focus:outline-none"
                    />
                  </label>
                  <label className="flex-1">
                    <span className="block text-[13px] text-ink-100">To</span>
                    <input
                      type="date"
                      value={prefs.rangeEnd}
                      onChange={(e) => setPrefs({ ...prefs, rangeEnd: e.target.value })}
                      className="w-full rounded-md border border-ink-400 bg-ink-900 px-2 py-1 text-[12px] text-ink-50 focus:border-emerald-500 focus:outline-none"
                    />
                  </label>
                </div>
              ) : (
                <div />
              )}
              {prefs.daysMode === "specific_days" ? (
                <div>
                  <span className="block text-[13px] text-ink-100">Available days</span>
                  <div className="flex gap-1">
                    {dayLabels.map((d, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => toggleDay(i)}
                        className={`h-6 w-6 rounded text-[13px] ${
                          prefs.specificDays.has(i)
                            ? "bg-emerald-600 text-ink-50"
                            : "border border-ink-400 text-ink-100 hover:border-ink-300"
                        }`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div />
              )}
              {prefs.startTimeMode === "specific" ? (
                <label className="block">
                  <span className="block text-[13px] text-ink-100">Start time</span>
                  <input
                    type="time"
                    value={prefs.specificStart}
                    onChange={(e) => setPrefs({ ...prefs, specificStart: e.target.value })}
                    className="w-full rounded-md border border-ink-400 bg-ink-900 px-2 py-1 text-[12px] text-ink-50 focus:border-emerald-500 focus:outline-none"
                  />
                </label>
              ) : (
                <div />
              )}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function RadioPair({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ v: string; l: string }>;
}): JSX.Element {
  return (
    <div>
      <span className="block text-[13px] text-ink-100">{label}</span>
      <div className="flex gap-3">
        {options.map((opt) => (
          <label key={opt.v} className="flex items-center gap-1 text-[12px]">
            <input
              type="radio"
              checked={value === opt.v}
              onChange={() => onChange(opt.v)}
              className="accent-emerald-500"
            />
            {opt.l}
          </label>
        ))}
      </div>
    </div>
  );
}

// ─── §2 Slots Table ─────────────────────────────────────────────────────
//
// One row per (cleaner, day) — the EARLIEST slot of that day for that
// cleaner. Subsequent slots are stashed in `extras` and only rendered when
// the user clicks "+ N more". Sort is by slot_start ascending so the
// nearest-to-now option for whoever can do it first floats to the top.

interface SlotGroup {
  key: string; // `${tech_id}|${YYYY-MM-DD}`
  head: ScheduleSlotResult;
  extras: ScheduleSlotResult[]; // sorted asc, excludes head
}

function groupSlotsByTechDay(slots: ScheduleSlotResult[]): SlotGroup[] {
  const byKey = new Map<string, ScheduleSlotResult[]>();
  for (const s of slots) {
    const dayKey = s.slot_start.slice(0, 10);
    const k = `${s.tech_id}|${dayKey}`;
    const arr = byKey.get(k);
    if (arr) {
      arr.push(s);
    } else {
      byKey.set(k, [s]);
    }
  }
  const groups: SlotGroup[] = [];
  for (const [k, arr] of byKey) {
    arr.sort((a, b) => a.slot_start.localeCompare(b.slot_start));
    groups.push({ key: k, head: arr[0]!, extras: arr.slice(1) });
  }
  groups.sort((a, b) => a.head.slot_start.localeCompare(b.head.slot_start));
  return groups;
}

function renderTravelCell(s: ScheduleSlotResult, isOver: boolean): ReactNode {
  const travelMin = s.travel_minutes ?? 0;
  const colour = isOver ? "text-pink-300" : "text-ink-200";
  if (s.travel_origin === "previous_unknown") {
    return (
      <span className={`tabular-nums ${colour}`}>
        {travelMin}m
        <span className="ml-1 text-[13px] text-ink-100">default</span>
      </span>
    );
  }
  const homeOrFrom = s.travel_origin === "home"
    ? "from home"
    : `from ${s.travel_from_postcode ?? "?"}`;
  if (s.distance_miles == null) {
    return (
      <span className={`tabular-nums ${colour}`}>
        {travelMin}m
        <span className="ml-1 text-[13px] text-ink-100">{homeOrFrom}</span>
      </span>
    );
  }
  return (
    <span className={`tabular-nums ${colour}`}>
      {travelMin}m · {s.distance_miles.toFixed(1)}mi
      <span className="ml-1 text-[13px] text-ink-100">{homeOrFrom}</span>
    </span>
  );
}

function SlotsTablePanel({
  slots,
  billableValueGbp,
  durationMinutes,
  showOver,
  setShowOver,
  onPick,
  onJumpToDate,
  loading,
  rejectedCount,
}: {
  slots: ScheduleSlotResult[];
  billableValueGbp: number;
  durationMinutes: number;
  showOver: boolean;
  setShowOver: (v: boolean) => void;
  onPick: (s: ScheduleSlotResult) => void;
  onJumpToDate: (isoDate: string) => void;
  loading: boolean;
  rejectedCount: number;
}): JSX.Element {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState<boolean>(false);

  const under = slots.filter((s) => (s.travel_minutes ?? 0) <= OVER_THRESHOLD_MIN);
  const over = slots.filter((s) => (s.travel_minutes ?? 0) > OVER_THRESHOLD_MIN);
  const visible = showOver ? slots : under;
  const groups = useMemo(() => groupSlotsByTechDay(visible), [visible]);

  function slotKey(s: ScheduleSlotResult): string {
    return `${s.tech_id}|${s.slot_start}`;
  }
  function toggleExpanded(groupKey: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  }
  function toggleSelected(s: ScheduleSlotResult): void {
    setSelected((prev) => {
      const next = new Set(prev);
      const k = slotKey(s);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }
  async function copyToClipboard(): Promise<void> {
    if (selected.size === 0) return;
    // Walk the table in displayed order (date asc, with extras inline below
    // their head) so the clipboard text reads top-to-bottom like the user
    // sees it.
    const orderedIsos: string[] = [];
    for (const g of groups) {
      if (selected.has(slotKey(g.head))) orderedIsos.push(g.head.slot_start);
      for (const e of g.extras) {
        if (selected.has(slotKey(e))) orderedIsos.push(e.slot_start);
      }
    }
    const lines = ["Here are some dates and times:", ...orderedIsos.map(fmtClipboardLine)];
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Browsers without clipboard API (or denied perms) — fall back to
      // textarea-select as a courtesy. Failure here isn't worth surfacing.
    }
  }

  if (loading) {
    return (
      <section className="border-b border-ink-600 px-4 py-6 text-center text-[13px] text-ink-200">
        Searching slots…
      </section>
    );
  }
  if (slots.length === 0) {
    return (
      <section className="border-b border-ink-600 px-4 py-6">
        <div className="mb-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
          Best slots
        </div>
        <div className="rounded-md border border-amber-700 bg-amber-950/20 px-3 py-2 text-[12px] text-amber-200">
          No slots found. {rejectedCount} candidates rejected. Try adjusting the
          postcode, duration, or value above.
        </div>
      </section>
    );
  }
  return (
    <section className="border-b border-ink-600 px-4 py-3">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
          Best slots
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void copyToClipboard()}
            disabled={selected.size === 0}
            className="rounded-md border border-emerald-600 bg-emerald-700/70 px-2 py-1 text-[13px] text-ink-50 hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {copied ? "Copied ✓" : `Copy selected (${selected.size})`}
          </button>
          <label className="flex items-center gap-1 text-[13px] text-ink-100">
            <input
              type="checkbox"
              checked={showOver}
              onChange={(e) => setShowOver(e.target.checked)}
              className="accent-emerald-500"
            />
            Show longer drives (&gt;35 min) — {over.length} hidden
          </label>
        </div>
      </div>
      <div className="max-h-[28vh] overflow-y-auto rounded-md border border-ink-600">
        <table className="w-full text-[14px]">
          <thead className="sticky top-0 border-b border-ink-600 bg-ink-800/95 text-[13px] text-ink-100">
            <tr>
              <th className="px-2 py-2 text-left font-medium" style={{ width: 32 }}></th>
              <th className="px-2 py-2 text-left font-medium">Cleaner</th>
              <th className="px-2 py-2 text-left font-medium">Day · Date</th>
              <th className="px-2 py-2 text-left font-medium">Time</th>
              <th className="px-2 py-2 text-left font-medium">Travel</th>
              <th className="px-2 py-2 text-left font-medium">Total</th>
              <th className="px-2 py-2 text-right font-medium">Total £</th>
              <th className="px-2 py-2 text-left font-medium">£/hr</th>
              <th className="px-2 py-2 text-right font-medium" style={{ width: 64 }}></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-700/60">
            {groups.flatMap((g) => {
              const headRow = (
                <SlotRow
                  key={g.key + ":head"}
                  slot={g.head}
                  billableValueGbp={billableValueGbp}
                  durationMinutes={durationMinutes}
                  selected={selected.has(slotKey(g.head))}
                  onToggleSelected={() => toggleSelected(g.head)}
                  onPick={() => onPick(g.head)}
                  onJumpToDate={() => onJumpToDate(g.head.slot_start.slice(0, 10))}
                  isExtra={false}
                  trailing={
                    g.extras.length > 0 ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpanded(g.key);
                        }}
                        className="whitespace-nowrap rounded border border-ink-400 px-2 py-0.5 text-[13px] text-ink-100 hover:border-emerald-500"
                      >
                        {expanded.has(g.key) ? `– hide` : `+ ${g.extras.length}`}
                      </button>
                    ) : null
                  }
                />
              );
              if (!expanded.has(g.key)) return [headRow];
              const extraRows = g.extras.map((s) => (
                <SlotRow
                  key={g.key + ":" + s.slot_start}
                  slot={s}
                  billableValueGbp={billableValueGbp}
                  durationMinutes={durationMinutes}
                  selected={selected.has(slotKey(s))}
                  onToggleSelected={() => toggleSelected(s)}
                  onPick={() => onPick(s)}
                  onJumpToDate={() => onJumpToDate(s.slot_start.slice(0, 10))}
                  isExtra={true}
                  trailing={null}
                />
              ));
              return [headRow, ...extraRows];
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SlotRow({
  slot,
  billableValueGbp,
  durationMinutes,
  selected,
  onToggleSelected,
  onPick,
  onJumpToDate,
  isExtra,
  trailing,
}: {
  slot: ScheduleSlotResult;
  billableValueGbp: number;
  durationMinutes: number;
  selected: boolean;
  onToggleSelected: () => void;
  onPick: () => void;
  onJumpToDate: () => void;
  isExtra: boolean;
  trailing: ReactNode;
}): JSX.Element {
  const travelMin = slot.travel_minutes ?? 0;
  const totalMin = durationMinutes + travelMin;
  const commission = slot.tech_commission_pct ?? 0;
  const techEarnings = (billableValueGbp * commission) / 100;
  const hourlyMin = Math.max(60, totalMin);
  const hourly = techEarnings / (hourlyMin / 60);
  const isOver = travelMin > OVER_THRESHOLD_MIN;
  const lowEarn = commission > 0 && hourly < 15;
  const rowBg = isExtra
    ? "bg-ink-900/40"
    : isOver
      ? "bg-pink-950/20"
      : "";
  return (
    <tr
      onClick={onPick}
      className={`cursor-pointer hover:bg-ink-800/80 ${rowBg}`}
    >
      <td
        className="px-2 py-1"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelected}
          className="accent-emerald-500"
        />
      </td>
      <td className={`px-2 py-1 ${isExtra ? "text-ink-200" : "text-ink-50"}`}>
        {isExtra ? <span className="mr-1 text-ink-100">·</span> : null}
        {slot.tech_name}
        {commission > 0 ? (
          <span className="ml-1 text-[13px] text-ink-100">({commission.toFixed(0)}%)</span>
        ) : null}
      </td>
      <td
        className="cursor-pointer px-2 py-2 text-emerald-300 underline decoration-dotted underline-offset-2 hover:text-emerald-200"
        onClick={(e) => {
          e.stopPropagation();
          onJumpToDate();
        }}
        title="Click to jump the day view below to this date"
      >
        {fmtDayHeader(slot.slot_start)}
      </td>
      <td className="px-2 py-2 text-ink-200 tabular-nums">
        {isoHM(slot.slot_start)}–{isoHM(slot.slot_end)}
      </td>
      <td className="px-2 py-2">{renderTravelCell(slot, isOver)}</td>
      <td className="px-2 py-2 text-ink-200 tabular-nums">{fmtHrs(totalMin)}</td>
      <td className="px-2 py-2 text-right text-ink-200 tabular-nums">
        £{billableValueGbp.toFixed(2)}
      </td>
      <td
        className={`px-2 py-1 tabular-nums ${
          commission === 0 ? "text-ink-100" : lowEarn ? "text-red-400" : "text-emerald-300"
        }`}
      >
        {commission > 0 ? `£${hourly.toFixed(2)}/hr` : "—"}
      </td>
      <td className="px-2 py-2 text-right">{trailing}</td>
    </tr>
  );
}

// ─── §3 Day Grid ─────────────────────────────────────────────────────────

interface OverlaySlot {
  tech_id: number;
  start: string;
  end: string;
  is_over: boolean;
  score: number;
  travel_minutes: number;
  distance_miles: number | null;
  travel_from: string | null;
}

interface RescheduleProposal {
  vid: number;
  customer_name: string | null;
  postcode: string | null;
  durationMinutes: number;
  fromTechId: number;
  fromTechName: string;
  fromStart: string;
  fromEnd: string;
  toTechId: number;
  toTechName: string;
  toStart: string;
  toEnd: string;
}

function DayGrid({
  date,
  setDate,
  techs,
  loading,
  overlay,
  showOver,
  onProposeReschedule,
  onGridPick,
  onOverlayPick,
  pickEnabled,
}: {
  date: string;
  setDate: (s: string) => void;
  techs: DispatchTech[];
  loading: boolean;
  overlay: OverlaySlot[];
  showOver: boolean;
  onProposeReschedule: (p: RescheduleProposal) => void;
  onGridPick: (techId: number, techName: string, minutesFromDayStart: number) => void;
  onOverlayPick: (slot: OverlaySlot, techName: string) => void;
  pickEnabled: boolean;
}): JSX.Element {
  // `overlay` is now pre-filtered upstream to one earliest slot per (tech, date),
  // so no merging is needed — render each slot as a single column-width block.
  return (
    <section className="px-4 py-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
          Day view — {parseIsoDate(date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setDate(shiftDate(date, -1))}
            className="rounded-md border border-ink-400 px-2 py-0.5 text-[12px] text-ink-100 hover:border-ink-300"
          >
            ◀
          </button>
          <button
            type="button"
            onClick={() => setDate(todayIso())}
            className="rounded-md border border-ink-400 px-2 py-0.5 text-[12px] text-ink-100 hover:border-ink-300"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => setDate(shiftDate(date, 1))}
            className="rounded-md border border-ink-400 px-2 py-0.5 text-[12px] text-ink-100 hover:border-ink-300"
          >
            ▶
          </button>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="rounded-md border border-ink-400 bg-ink-900 px-2 py-0.5 text-[12px] text-ink-50 focus:border-emerald-500 focus:outline-none"
          />
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center text-[12px] text-ink-200">Loading day…</div>
      ) : techs.length === 0 ? (
        <div className="py-8 text-center text-[12px] text-ink-200">No active technicians.</div>
      ) : (
        <div className="flex gap-2 overflow-x-auto">
          {/* Hour ruler */}
          <div className="shrink-0 pt-12" style={{ width: 44 }}>
            {HOUR_LABELS.map((h) => (
              <div
                key={h}
                className="text-right pr-1 text-[12px] text-ink-100"
                style={{ height: 60 * PIXELS_PER_MIN, lineHeight: "1" }}
              >
                {String(h).padStart(2, "0")}:00
              </div>
            ))}
          </div>

          {/* Tech columns */}
          {techs.map((tech) => {
            const overlayForTech = overlay.filter((o) => o.tech_id === tech.tech_id);
            return (
              <TechColumn
                key={tech.tech_id}
                tech={tech}
                date={date}
                overlayForTech={overlayForTech}
                showOver={showOver}
                onProposeReschedule={onProposeReschedule}
                onGridPick={onGridPick}
                onOverlayPick={onOverlayPick}
                pickEnabled={pickEnabled}
              />
            );
          })}
        </div>
      )}

      {/* Legend */}
      <div className="mt-3 flex flex-wrap gap-3 text-[13px] text-ink-100">
        <LegendSwatch className="bg-emerald-700/70 border-emerald-600" label="Booked visit" />
        <LegendSwatch className="bg-red-700/40 border-red-700/70" label="Free/busy (tech app)" />
        <LegendSwatch className="bg-slate-600/40 border-slate-500/70" label="Free/busy (calendar)" />
        <LegendSwatch className="bg-amber-500/50 border-amber-400" label="Algo recommended" />
        <LegendSwatch className="bg-pink-600/55 border-pink-400" label="Recommended (>35 min drive)" />
      </div>
    </section>
  );
}

function LegendSwatch({ className, label }: { className: string; label: string }): JSX.Element {
  return (
    <div className="flex items-center gap-1">
      <span className={`inline-block h-3 w-4 rounded-sm border ${className}`} />
      <span>{label}</span>
    </div>
  );
}

function TechColumn({
  tech,
  date,
  overlayForTech,
  showOver,
  onProposeReschedule,
  onGridPick,
  onOverlayPick,
  pickEnabled,
}: {
  tech: DispatchTech;
  date: string;
  overlayForTech: OverlaySlot[];
  showOver: boolean;
  onProposeReschedule: (p: RescheduleProposal) => void;
  onGridPick: (techId: number, techName: string, minutesFromDayStart: number) => void;
  onOverlayPick: (slot: OverlaySlot, techName: string) => void;
  pickEnabled: boolean;
}): JSX.Element {
  const colRef = useRef<HTMLDivElement | null>(null);
  const [dropPreviewMin, setDropPreviewMin] = useState<number | null>(null);
  const [hoverPreviewMin, setHoverPreviewMin] = useState<number | null>(null);

  function handleDragOver(ev: React.DragEvent<HTMLDivElement>): void {
    if (!ev.dataTransfer.types.includes("application/x-breeze-visit")) return;
    ev.preventDefault();
    ev.dataTransfer.dropEffect = "move";
    if (!colRef.current) return;
    const rect = colRef.current.getBoundingClientRect();
    const yPx = ev.clientY - rect.top;
    const min = snap30(Math.max(0, Math.min(COL_HEIGHT, yPx) / PIXELS_PER_MIN));
    setDropPreviewMin(min);
  }
  function handleDragLeave(): void {
    setDropPreviewMin(null);
  }
  function handleColumnClick(ev: React.MouseEvent<HTMLDivElement>): void {
    if (!pickEnabled) return;
    if (ev.target !== ev.currentTarget) return;
    if (!colRef.current) return;
    const rect = colRef.current.getBoundingClientRect();
    const yPx = ev.clientY - rect.top;
    const min = snap30(Math.max(0, Math.min(COL_HEIGHT, yPx) / PIXELS_PER_MIN));
    onGridPick(tech.tech_id, tech.tech_name, min);
  }
  function handleColumnMouseMove(ev: React.MouseEvent<HTMLDivElement>): void {
    if (!pickEnabled) return;
    if (ev.target !== ev.currentTarget) {
      setHoverPreviewMin(null);
      return;
    }
    if (!colRef.current) return;
    const rect = colRef.current.getBoundingClientRect();
    const yPx = ev.clientY - rect.top;
    const min = snap30(Math.max(0, Math.min(COL_HEIGHT, yPx) / PIXELS_PER_MIN));
    setHoverPreviewMin(min);
  }

  function handleDrop(ev: React.DragEvent<HTMLDivElement>): void {
    ev.preventDefault();
    setDropPreviewMin(null);
    const raw = ev.dataTransfer.getData("application/x-breeze-visit");
    if (!raw) return;
    let payload: {
      vid: number;
      tech_id: number;
      tech_name: string;
      start: string;
      end: string;
      customer_name: string | null;
      postcode: string | null;
    };
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }
    if (!colRef.current) return;
    const rect = colRef.current.getBoundingClientRect();
    const yPx = ev.clientY - rect.top;
    const minutes = snap30(Math.max(0, Math.min(COL_HEIGHT, yPx) / PIXELS_PER_MIN));

    const startMs = new Date(payload.start).getTime();
    const endMs = new Date(payload.end).getTime();
    const durationMinutes = Math.max(15, Math.round((endMs - startMs) / 60000));

    const toStart = combineLocal(date, minutes);
    const toEndDt = new Date(toStart);
    toEndDt.setMinutes(toEndDt.getMinutes() + durationMinutes);
    const toEnd = `${toEndDt.getFullYear()}-${pad2(toEndDt.getMonth() + 1)}-${pad2(toEndDt.getDate())}T${pad2(toEndDt.getHours())}:${pad2(toEndDt.getMinutes())}:00`;

    onProposeReschedule({
      vid: payload.vid,
      customer_name: payload.customer_name,
      postcode: payload.postcode,
      durationMinutes,
      fromTechId: payload.tech_id,
      fromTechName: payload.tech_name,
      fromStart: payload.start,
      fromEnd: payload.end,
      toTechId: tech.tech_id,
      toTechName: tech.tech_name,
      toStart,
      toEnd,
    });
  }

  const ts = tech.travel_summary;
  return (
    <div className="shrink-0" style={{ width: 220 }}>
      <div className="mb-1 rounded-t-md bg-ink-800 px-2 py-1 text-center">
        <div className="text-[13px] font-medium text-ink-50">
          {tech.tech_name}
          {tech.tech_commission_pct != null ? (
            <span className="ml-1 text-[12px] font-normal text-ink-100">
              ({tech.tech_commission_pct.toFixed(0)}%)
            </span>
          ) : null}
        </div>
        {ts ? (
          <>
            <div className="text-[12px] font-normal text-ink-100">
              {ts.miles_from_home.toFixed(1)} miles
            </div>
            <div className="text-[12px] font-normal text-ink-100">
              {ts.minutes_from_home} mins
            </div>
          </>
        ) : null}
      </div>
      <div
        ref={colRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleColumnClick}
        onMouseMove={handleColumnMouseMove}
        onMouseLeave={() => setHoverPreviewMin(null)}
        className={`relative rounded-b-md border border-ink-600 bg-ink-900 ${
          pickEnabled ? "cursor-pointer" : ""
        }`}
        style={{ height: COL_HEIGHT }}
      >
        {/* Hour gridlines */}
        {HOUR_LABELS.slice(1).map((h) => (
          <div
            key={h}
            className="pointer-events-none absolute left-0 right-0 border-t border-ink-700"
            style={{ top: (h - DAY_START_HOUR) * 60 * PIXELS_PER_MIN }}
          />
        ))}

        {/* Drop-position preview */}
        {dropPreviewMin !== null ? (
          <div
            className="pointer-events-none absolute left-0 right-0 border-t-2 border-amber-300"
            style={{ top: dropPreviewMin * PIXELS_PER_MIN }}
          >
            <div className="absolute -top-4 right-0 rounded bg-amber-500/80 px-1 py-0.5 text-[12px] text-ink-950">
              drop @ {pad2(DAY_START_HOUR + Math.floor(dropPreviewMin / 60))}:
              {pad2(dropPreviewMin % 60)}
            </div>
          </div>
        ) : null}

        {/* Click-position preview */}
        {pickEnabled && hoverPreviewMin !== null && dropPreviewMin === null ? (
          <div
            className="pointer-events-none absolute left-0 right-0 border-t-2 border-emerald-400/70"
            style={{ top: hoverPreviewMin * PIXELS_PER_MIN }}
          >
            <div className="absolute -top-4 right-0 rounded bg-emerald-500/80 px-1 py-0.5 text-[12px] text-ink-950">
              click to book @ {pad2(DAY_START_HOUR + Math.floor(hoverPreviewMin / 60))}:
              {pad2(hoverPreviewMin % 60)}
            </div>
          </div>
        ) : null}

        {/* Blocks */}
        {tech.blocks.map((b, idx) => (
          <BlockCell key={`b-${idx}`} block={b} techId={tech.tech_id} techName={tech.tech_name} />
        ))}

        {/* Earliest algo-recommended slot for this tech (one per day) */}
        {overlayForTech
          .filter((o) => showOver || !o.is_over)
          .map((o, idx) => (
            <OverlayCell
              key={`o-${idx}`}
              overlay={o}
              clickable={pickEnabled}
              onPick={() => onOverlayPick(o, tech.tech_name)}
            />
          ))}
      </div>
    </div>
  );
}

function BlockCell({ block, techId, techName }: { block: DispatchBlock; techId: number; techName: string }): JSX.Element | null {
  const sMin = minutesFromDayStart(block.start);
  const eMin = minutesFromDayStart(block.end);
  const pos = clampToCol(sMin, eMin);
  if (!pos) return null;

  let cls = "";
  let label: ReactNode = null;
  let title = "";
  let draggable = false;
  let onDragStart: ((ev: React.DragEvent<HTMLDivElement>) => void) | undefined;

  const tj = block.travel_from_job;
  const travelLine = tj ? `${tj.minutes} mins · ${tj.miles.toFixed(1)} miles` : null;

  if (block.kind === "visit") {
    cls = "bg-emerald-700/70 border-emerald-600 text-emerald-50 hover:bg-emerald-700/90 cursor-grab active:cursor-grabbing";
    label = (
      <div className="px-1 py-0.5">
        <div className="truncate text-[12px] font-medium">
          #{block.vid} · {block.customer_name ?? "(no name)"}
        </div>
        <div className="truncate text-[12px] opacity-80">{block.postcode ?? ""}</div>
        <div className="text-[12px] opacity-70">
          {isoHM(block.start)}–{isoHM(block.end)}
        </div>
        {travelLine ? (
          <div className="truncate text-[12px] opacity-80">{travelLine} from job</div>
        ) : null}
      </div>
    );
    title = `VID ${block.vid} · ${block.customer_name ?? ""} · ${block.postcode ?? ""} · £${block.value_gbp?.toFixed(0) ?? "?"}${travelLine ? ` · ${travelLine} from job` : ""}\n(drag to reschedule)`;
    draggable = true;
    onDragStart = (ev) => {
      ev.dataTransfer.effectAllowed = "move";
      ev.dataTransfer.setData(
        "application/x-breeze-visit",
        JSON.stringify({
          vid: block.vid,
          tech_id: techId,
          tech_name: techName,
          start: block.start,
          end: block.end,
          customer_name: block.customer_name,
          postcode: block.postcode,
        })
      );
    };
  } else {
    const isAvail = block.source === "B_TAVAILABILITY";
    cls = isAvail
      ? "bg-red-700/40 border-red-700/70 text-red-100 hover:bg-red-700/55"
      : "bg-slate-600/40 border-slate-500/70 text-slate-100 hover:bg-slate-600/55";
    label = (
      <div className="px-1 py-0.5">
        <div className="truncate text-[12px]">
          {isAvail ? "Busy (tech app)" : "Busy (calendar)"}
        </div>
        {block.postcode ? (
          <div className="truncate text-[12px] opacity-80">{block.postcode}</div>
        ) : null}
        <div className="text-[12px] opacity-70">
          {block.all_day ? "all day" : `${isoHM(block.start)}–${isoHM(block.end)}`}
        </div>
        {travelLine ? (
          <div className="truncate text-[12px] opacity-80">{travelLine} from job</div>
        ) : null}
      </div>
    );
    title = `${block.source} ${block.all_day ? "all day" : `${isoHM(block.start)}–${isoHM(block.end)}`}${travelLine ? ` · ${travelLine} from job` : ""}`;
  }

  return (
    <div
      title={title}
      draggable={draggable}
      onDragStart={onDragStart}
      onClick={(e) => e.stopPropagation()}
      className={`absolute left-0.5 right-0.5 overflow-hidden rounded border ${cls}`}
      style={{ top: pos.top, height: pos.height }}
    >
      {label}
    </div>
  );
}

/** Renders one algo-recommended slot per tech per day. Earliest only —
 *  full column width, easy to read, click anywhere on the block to pick it.
 *  The §2 Best Slots table is where every other option for that tech-day
 *  lives. Always shows the travel context (miles + mins) so the agent can
 *  compare cleaners at a glance. */
function OverlayCell({
  overlay,
  clickable,
  onPick,
}: {
  overlay: OverlaySlot;
  clickable: boolean;
  onPick: () => void;
}): JSX.Element | null {
  const sMin = minutesFromDayStart(overlay.start);
  const eMin = minutesFromDayStart(overlay.end);
  const pos = clampToCol(sMin, eMin);
  if (!pos) return null;
  const cls = overlay.is_over
    ? "bg-pink-600/55 border-pink-400 text-pink-50"
    : "bg-amber-500/55 border-amber-400 text-amber-50";
  const milesStr =
    overlay.distance_miles != null ? `${overlay.distance_miles.toFixed(1)} miles` : null;
  const travelStr = milesStr
    ? `${overlay.travel_minutes} mins · ${milesStr}`
    : `${overlay.travel_minutes} mins`;
  const title = clickable
    ? `Click to book · earliest recommended ${isoHM(overlay.start)}–${isoHM(overlay.end)} · ${travelStr}`
    : `Earliest recommended ${isoHM(overlay.start)}–${isoHM(overlay.end)} · ${travelStr}`;
  return (
    <div
      title={title}
      onClick={
        clickable
          ? (e) => {
              e.stopPropagation();
              onPick();
            }
          : undefined
      }
      className={`absolute left-0.5 right-0.5 overflow-hidden rounded border ${cls} ${
        clickable ? "cursor-pointer hover:brightness-125" : ""
      }`}
      style={{ top: pos.top, height: pos.height }}
    >
      <div className="px-1.5 py-0.5 text-[13px] font-semibold">
        {isoHM(overlay.start)}
      </div>
      <div className="px-1.5 text-[12px] opacity-90">
        {travelStr}
      </div>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────

export interface SlotPick {
  techId: number;
  techName: string;
  scheduleDate: string;
  startTime: string;
  endTime: string;
}

export interface DispatchBoard5Props {
  onClose: () => void;
  /** Card ID the modal is bound to — required for saving the
   *  "Discussed date/time notes" textarea as a card activity. When null
   *  (e.g. opened from an unsaved quote), the Save note button is disabled
   *  with a tooltip explaining why. */
  cardId: number | null;
  initialPostcode?: string;
  initialDurationMinutes?: number;
  initialEstimatedValueGbp?: number;
  initialDate?: string;
  onPickSlot?: (pick: SlotPick) => void;
}

export function DispatchBoard5({
  onClose,
  cardId,
  initialPostcode,
  initialDurationMinutes,
  initialEstimatedValueGbp,
  initialDate,
  onPickSlot,
}: DispatchBoard5Props): JSX.Element {
  const startDate =
    initialDate && /^\d{4}-\d{2}-\d{2}$/.test(initialDate) ? initialDate : todayIso();
  const [date, setDate] = useState<string>(startDate);
  const [postcode, setPostcode] = useState<string>(initialPostcode ?? "");
  const [durationMinutes, setDurationMinutes] = useState<number>(initialDurationMinutes ?? 60);
  const [estimatedValueGbp, setEstimatedValueGbp] = useState<number>(initialEstimatedValueGbp ?? 80);
  const [prefs, setPrefs] = useState<PrefsState>(EMPTY_PREFS);
  const [showOver, setShowOver] = useState<boolean>(false);

  // Note-save lifecycle (Item 1)
  const [noteSaving, setNoteSaving] = useState<boolean>(false);
  const [noteSaved, setNoteSaved] = useState<boolean>(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  async function saveNote(): Promise<void> {
    if (cardId == null) return;
    const text = prefs.notes.trim();
    if (!text) return;
    setNoteSaving(true);
    setNoteSaved(false);
    setNoteError(null);
    try {
      await postNote(cardId, { note: `📅 Discussed availability: ${text}` });
      setNoteSaved(true);
      window.setTimeout(() => setNoteSaved(false), 2500);
    } catch (e) {
      setNoteError(e instanceof Error ? e.message : String(e));
    } finally {
      setNoteSaving(false);
    }
  }

  const [board, setBoard] = useState<DispatchBoardResponse | null>(null);
  const [boardLoading, setBoardLoading] = useState<boolean>(false);
  const [boardError, setBoardError] = useState<string | null>(null);
  const [boardReloadKey, setBoardReloadKey] = useState<number>(0);

  const [searchActive, setSearchActive] = useState<boolean>(false);
  const slotsQuery = useSlotsQuery({
    postcode,
    durationMinutes,
    estimatedValueGbp,
    jobRef: "DB5",
    enabled: searchActive,
  });
  const slotsResp = slotsQuery.data ?? null;
  const slotsLoading = searchActive && (slotsQuery.isPending || slotsQuery.isFetching);
  const slotsError = slotsQuery.error
    ? slotsQuery.error instanceof Error
      ? slotsQuery.error.message
      : String(slotsQuery.error)
    : null;
  const invalidateSlots = useInvalidateSlots();

  const [reschedule, setReschedule] = useState<RescheduleProposal | null>(null);
  const [rescheduleErr, setRescheduleErr] = useState<string | null>(null);
  const [rescheduleSaving, setRescheduleSaving] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    setBoardLoading(true);
    setBoardError(null);
    const pc = postcode.trim();

    async function loadWithRetry(): Promise<void> {
      try {
        const r = await fetchDispatchBoard(date, 1, pc || undefined);
        if (!cancelled) setBoard(r);
      } catch (firstErr) {
        if (cancelled) return;
        const msg = firstErr instanceof Error ? firstErr.message : String(firstErr);
        if (/502|upstream|abort|network/i.test(msg)) {
          await new Promise((res) => setTimeout(res, 800));
          if (cancelled) return;
          try {
            const r2 = await fetchDispatchBoard(date, 1, pc || undefined);
            if (!cancelled) setBoard(r2);
            return;
          } catch (secondErr) {
            if (cancelled) return;
            setBoardError(secondErr instanceof Error ? secondErr.message : String(secondErr));
            return;
          }
        }
        setBoardError(msg);
      } finally {
        if (!cancelled) setBoardLoading(false);
      }
    }
    void loadWithRetry();

    return () => {
      cancelled = true;
    };
  }, [date, boardReloadKey, postcode]);

  function runSearch(): void {
    if (!postcode.trim()) return;
    setSearchActive(true);
    void slotsQuery.refetch();
  }

  useEffect(() => {
    if (initialPostcode && initialPostcode.trim()) {
      setSearchActive(true);
    }
  }, [initialPostcode]);

  async function commitReschedule(force: boolean): Promise<void> {
    if (!reschedule) return;
    setRescheduleSaving(true);
    setRescheduleErr(null);
    try {
      await rescheduleVisit({
        vid: reschedule.vid,
        tech_id: reschedule.toTechId,
        new_start: reschedule.toStart,
        new_end: reschedule.toEnd,
        force,
      });
      setReschedule(null);
      void invalidateSlots();
      const movedDate = reschedule.toStart.split("T")[0];
      if (movedDate && movedDate !== date) {
        setDate(movedDate);
      } else {
        setBoardReloadKey((k) => k + 1);
      }
    } catch (e) {
      setRescheduleErr(e instanceof Error ? e.message : String(e));
    } finally {
      setRescheduleSaving(false);
    }
  }

  // Overlay for current day, fed from the slots search.
  const overlay: OverlaySlot[] = useMemo(() => {
    if (!slotsResp) return [];
    // Only the earliest recommended slot per cleaner per day — the algo
    // already returns overlapping 30-min candidates that visually clutter
    // the column. The §2 Best Slots table shows all options; §3 just
    // marks "where the next opening is" for each tech.
    const earliestPerTech = new Map<number, ScheduleSlotResult>();
    for (const s of slotsResp.slots) {
      if (!s.slot_start.startsWith(date)) continue;
      const existing = earliestPerTech.get(s.tech_id);
      if (!existing || s.slot_start < existing.slot_start) {
        earliestPerTech.set(s.tech_id, s);
      }
    }
    return [...earliestPerTech.values()].map((s) => ({
      tech_id: s.tech_id,
      start: s.slot_start,
      end: s.slot_end,
      is_over: (s.travel_minutes ?? 0) > OVER_THRESHOLD_MIN,
      score: s.score,
      travel_minutes: s.travel_minutes ?? 0,
      distance_miles: s.distance_miles,
      travel_from:
        s.travel_origin === "home" ? "home" : s.travel_from_postcode,
    }));
  }, [slotsResp, date]);

  function handlePickSlot(s: ScheduleSlotResult): void {
    const pickedDate = s.slot_start.split("T")[0] ?? "";
    const startTime = isoHM(s.slot_start);
    const endTime = snapHmTo30(isoHM(s.slot_end));
    if (onPickSlot) {
      onPickSlot({
        techId: s.tech_id,
        techName: s.tech_name,
        scheduleDate: pickedDate,
        startTime,
        endTime,
      });
      onClose();
      return;
    }
    if (pickedDate && pickedDate !== date) setDate(pickedDate);
  }

  function handleGridPick(techId: number, techName: string, minutesFromDayStart: number): void {
    if (!onPickSlot) return;
    const startTotal = DAY_START_HOUR * 60 + snap30(minutesFromDayStart);
    const startTime = `${pad2(Math.floor(startTotal / 60))}:${pad2(startTotal % 60)}`;
    // End time uses the SAME effective_job_minutes policy as the FastAPI
    // algo — cleaning + 30 overhead, min 60, rounded to 30 — so an
    // empty-grid click reserves the same block size the slot search would.
    const endTotal = startTotal + effectiveJobMinutes(durationMinutes);
    const endTime = `${pad2(Math.floor(endTotal / 60))}:${pad2(endTotal % 60)}`;
    onPickSlot({
      techId,
      techName,
      scheduleDate: date,
      startTime,
      endTime,
    });
    onClose();
  }

  function handleOverlayPick(sub: OverlaySlot, techName: string): void {
    if (!onPickSlot) return;
    onPickSlot({
      techId: sub.tech_id,
      techName,
      scheduleDate: sub.start.split("T")[0] ?? "",
      startTime: isoHM(sub.start),
      endTime: snapHmTo30(isoHM(sub.end)),
    });
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-2"
      onClick={onClose}
    >
      <div
        className="flex h-[96vh] w-[min(1480px,98vw)] flex-col overflow-hidden rounded-lg border border-ink-400 bg-ink-950 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-ink-600 bg-ink-900 px-4 py-2">
          <div>
            <div className="text-[15px] font-semibold text-ink-50">
              Dispatch Board 5
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-ink-400 px-2 py-1 text-[13px] text-ink-50 hover:border-ink-300"
          >
            Close
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <PreferencesPanel
            postcode={postcode}
            setPostcode={setPostcode}
            durationMinutes={durationMinutes}
            setDurationMinutes={setDurationMinutes}
            estimatedValueGbp={estimatedValueGbp}
            setEstimatedValueGbp={setEstimatedValueGbp}
            prefs={prefs}
            setPrefs={setPrefs}
            onSearch={runSearch}
            searching={slotsLoading}
            onSaveNote={() => void saveNote()}
            noteSaving={noteSaving}
            noteSaved={noteSaved}
            noteError={noteError}
            canSaveNote={cardId != null}
          />

          {(slotsResp || slotsLoading || slotsError) ? (
            slotsError ? (
              <section className="border-b border-ink-600 px-4 py-3">
                <div className="flex items-center justify-between rounded-md border border-red-700 bg-red-950/30 px-3 py-2 text-[12px] text-red-300">
                  <span>Slot search failed: {slotsError}</span>
                  <button
                    type="button"
                    onClick={() => void runSearch()}
                    className="rounded-md border border-red-600 bg-red-700/40 px-2 py-1 text-[12px] text-ink-50 hover:bg-red-700/60"
                  >
                    Retry
                  </button>
                </div>
              </section>
            ) : (
              <SlotsTablePanel
                slots={slotsResp?.slots ?? []}
                billableValueGbp={estimatedValueGbp}
                durationMinutes={durationMinutes}
                showOver={showOver}
                setShowOver={setShowOver}
                onPick={handlePickSlot}
                onJumpToDate={(iso) => setDate(iso)}
                loading={slotsLoading}
                rejectedCount={slotsResp?.rejected_count ?? 0}
              />
            )
          ) : null}

          {boardError ? (
            <section className="px-4 py-3">
              <div className="flex items-center justify-between rounded-md border border-red-700 bg-red-950/30 px-3 py-2 text-[12px] text-red-300">
                <span>Day-grid load failed: {boardError}</span>
                <button
                  type="button"
                  onClick={() => setBoardReloadKey((k) => k + 1)}
                  className="rounded-md border border-red-600 bg-red-700/40 px-2 py-1 text-[12px] text-ink-50 hover:bg-red-700/60"
                >
                  Retry
                </button>
              </div>
            </section>
          ) : (
            <DayGrid
              date={date}
              setDate={setDate}
              techs={board?.techs ?? []}
              loading={boardLoading}
              overlay={overlay}
              showOver={showOver}
              onProposeReschedule={(p) => {
                setRescheduleErr(null);
                setReschedule(p);
              }}
              onGridPick={handleGridPick}
              onOverlayPick={handleOverlayPick}
              pickEnabled={!!onPickSlot}
            />
          )}
        </div>

        {reschedule ? (
          <RescheduleConfirm
            proposal={reschedule}
            error={rescheduleErr}
            saving={rescheduleSaving}
            onConfirm={() => void commitReschedule(false)}
            onForce={() => void commitReschedule(true)}
            onCancel={() => {
              setReschedule(null);
              setRescheduleErr(null);
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

// ─── Reschedule confirm dialog ────────────────────────────────────────────

function RescheduleConfirm({
  proposal,
  error,
  saving,
  onConfirm,
  onForce,
  onCancel,
}: {
  proposal: RescheduleProposal;
  error: string | null;
  saving: boolean;
  onConfirm: () => void;
  onForce: () => void;
  onCancel: () => void;
}): JSX.Element {
  const movedDay = proposal.fromStart.split("T")[0] !== proposal.toStart.split("T")[0];
  const movedTech = proposal.fromTechId !== proposal.toTechId;
  const isOverlap = !!error && /OVERLAP|409/i.test(error);
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
      onClick={onCancel}
    >
      <div
        className="w-[min(520px,96vw)] rounded-lg border border-ink-400 bg-ink-900 p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 text-[14px] font-medium text-ink-50">
          Move booking #{proposal.vid}?
        </div>
        <div className="mb-1 text-[12px] text-ink-100">
          {proposal.customer_name ?? "(no name)"}
          {proposal.postcode ? ` · ${proposal.postcode}` : ""}
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3 text-[12px]">
          <div className="rounded border border-ink-600 bg-ink-800/60 px-3 py-2">
            <div className="text-ink-100">From</div>
            <div className="text-ink-50">{proposal.fromTechName}</div>
            <div className="text-ink-200">
              {fmtDayHeader(proposal.fromStart)} · {isoHM(proposal.fromStart)}–
              {isoHM(proposal.fromEnd)}
            </div>
          </div>
          <div
            className={`rounded border px-3 py-2 ${
              movedTech || movedDay
                ? "border-amber-500 bg-amber-950/20"
                : "border-ink-600 bg-ink-800/60"
            }`}
          >
            <div className="text-ink-100">To</div>
            <div className="text-ink-50">{proposal.toTechName}</div>
            <div className="text-ink-200">
              {fmtDayHeader(proposal.toStart)} · {isoHM(proposal.toStart)}–
              {isoHM(proposal.toEnd)}
            </div>
          </div>
        </div>

        {error ? (
          <div className="mt-3 rounded-md border border-red-700 bg-red-950/30 px-3 py-2 text-[12px] text-red-300">
            {error}
          </div>
        ) : null}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded-md border border-ink-400 px-3 py-1 text-[12px] text-ink-50 hover:border-ink-300 disabled:opacity-50"
          >
            Cancel
          </button>
          {isOverlap ? (
            <button
              type="button"
              onClick={onForce}
              disabled={saving}
              className="rounded-md border border-red-600 bg-red-700 px-3 py-1 text-[12px] font-medium text-ink-50 hover:bg-red-600 disabled:opacity-50"
            >
              {saving ? "Forcing…" : "Force override"}
            </button>
          ) : (
            <button
              type="button"
              onClick={onConfirm}
              disabled={saving}
              className="rounded-md border border-emerald-600 bg-emerald-700 px-3 py-1 text-[12px] font-medium text-ink-50 hover:bg-emerald-600 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Confirm move"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
